import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select, Checkbox, RadioGroup } from '../ui';
import { DynamicTable, Column } from './DynamicTable';
import { maskCPF, maskPhone } from '../../utils/masks';
import { SAFRA_OPTIONS, MARITAL_STATUS_OPTIONS } from '../../utils/constants';
import type { ClientData, GroupMember, MaritalStatus } from '../../types';

// Cria um novo membro de grupo com valores padrão
const createNewGroupMember = (): GroupMember => ({
  id: `member-${Date.now()}`,
  name: '',
  cpf: '',
  relationship: '',
  maritalStatus: 'solteiro',
});

export interface ClientDataSectionProps {
  data: Partial<ClientData>;
  onChange: (field: keyof ClientData) => (value: string | boolean) => void;
  onBulkChange?: (fields: Partial<ClientData>) => void;
  onGroupMembersChange: (members: GroupMember[]) => void;
  onContactPreferenceChange: (field: string) => (value: string | boolean) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
  prefilledName?: string;
  prefilledCpf?: string;
  prefilledPhone?: string;
}

export const ClientDataSection: React.FC<ClientDataSectionProps> = ({
  data,
  onChange,
  onBulkChange,
  onGroupMembersChange,
  onContactPreferenceChange,
  errors = {},
  disabled = false,
  prefilledName = '',
  prefilledCpf = '',
  prefilledPhone = '',
}) => {
  const { t } = useTranslation();

  // Flag "mesmos dados do cliente"
  const [sameAsClient, setSameAsClient] = useState(false);

  const handleSameAsClientChange = useCallback(
    (checked: boolean) => {
      setSameAsClient(checked);
      if (checked && prefilledName && prefilledCpf) {
        // Atualizar todos os campos em uma única chamada para evitar
        // que cada onChange sobrescreva o anterior (stale closure)
        if (onBulkChange) {
          onBulkChange({
            producerName: prefilledName,
            cpf: prefilledCpf,
            ...(prefilledPhone ? { phone: prefilledPhone } : {}),
          });
        } else {
          onChange('producerName')(prefilledName);
          onChange('cpf')(prefilledCpf);
          if (prefilledPhone) {
            onChange('phone')(prefilledPhone);
          }
        }
      } else if (!checked) {
        if (onBulkChange) {
          onBulkChange({ producerName: '', cpf: '', phone: '' });
        } else {
          onChange('producerName')('');
          onChange('cpf')('');
        }
      }
    },
    [onChange, onBulkChange, prefilledName, prefilledCpf, prefilledPhone]
  );

  // Controle de visibilidade das subseções
  const [showGroupSection, setShowGroupSection] = useState(data.plantsInGroup || false);
  const [showContactSection, setShowContactSection] = useState(
    data.contactPreference?.canContact || false
  );

  // Handler para radio "planta em grupo" — atualiza estado local e propaga
  const handlePlantsInGroupChange = useCallback(
    (value: string) => {
      const boolValue = value === 'true';
      setShowGroupSection(boolValue);
      onChange('plantsInGroup')(boolValue);
    },
    [onChange]
  );

  // Handler para radio "pode entrar em contato" — atualiza estado local e propaga
  const handleCanContactChange = useCallback(
    (value: string) => {
      const boolValue = value === 'true';
      setShowContactSection(boolValue);
      onContactPreferenceChange('canContact')(boolValue);
    },
    [onContactPreferenceChange]
  );

  // Colunas da tabela de membros do grupo
  const groupMemberColumns: Column<GroupMember>[] = useMemo(
    () => [
      {
        key: 'name',
        header: t('clientData.name'),
        width: '25%',
        render: (item, _index, onItemChange) => (
          <Input
            value={item.name}
            onChange={(value) => onItemChange({ ...item, name: value })}
            placeholder={t('clientData.name')}
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'cpf',
        header: t('clientData.cpf'),
        width: '20%',
        render: (item, _index, onItemChange) => (
          <Input
            value={item.cpf}
            onChange={(value) => onItemChange({ ...item, cpf: maskCPF(value) })}
            placeholder="CPF"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'relationship',
        header: t('clientData.relationship'),
        width: '20%',
        render: (item, _index, onItemChange) => (
          <Input
            value={item.relationship}
            onChange={(value) => onItemChange({ ...item, relationship: value })}
            placeholder="Ex: Filho, Pai"
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
      {
        key: 'maritalStatus',
        header: t('clientData.maritalStatus'),
        width: '35%',
        render: (item, _index, onItemChange) => (
          <Select
            value={item.maritalStatus}
            onChange={(value) =>
              onItemChange({ ...item, maritalStatus: value as MaritalStatus })
            }
            options={MARITAL_STATUS_OPTIONS}
            disabled={disabled}
            containerClassName="mb-0"
          />
        ),
      },
    ],
    [disabled, t]
  );

  return (
    <div>
      {/* Flag: mesmos dados do cliente */}
      {prefilledName && prefilledCpf && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Checkbox
            label={t('clientData.sameAsClient')}
            checked={sameAsClient}
            onChange={handleSameAsClientChange}
            disabled={disabled}
          />
        </div>
      )}

      {/* Campos principais do produtor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label={t('clientData.producerName')}
          value={data.producerName || ''}
          onChange={onChange('producerName')}
          placeholder="Nome completo"
          error={errors['clientData.producerName']}
          required
          disabled={disabled || sameAsClient}
        />

        <Input
          label={t('clientData.cpf')}
          value={data.cpf || ''}
          onChange={onChange('cpf')}
          mask={maskCPF}
          placeholder="000.000.000-00"
          error={errors['clientData.cpf']}
          required
          disabled={disabled || sameAsClient}
        />

        <Input
          label={t('clientData.phone')}
          value={data.phone || ''}
          onChange={onChange('phone')}
          mask={maskPhone}
          placeholder="(00) 00000-0000"
          error={errors['clientData.phone']}
          required
          disabled={disabled}
        />

        <Select
          label={t('clientData.financedSeason')}
          value={data.financedSeason || ''}
          onChange={onChange('financedSeason')}
          options={SAFRA_OPTIONS}
          error={errors['clientData.financedSeason']}
          required
          disabled={disabled}
        />
      </div>

      {/* Pergunta sobre plantio em grupo */}
      <div className="mt-4">
        <RadioGroup
          name="plantsInGroup"
          label={t('clientData.plantsInGroup')}
          options={[
            { value: 'true', label: t('common.yes') },
            { value: 'false', label: t('common.no') },
          ]}
          value={data.plantsInGroup?.toString() || 'false'}
          onChange={handlePlantsInGroupChange}
          orientation="horizontal"
          disabled={disabled}
        />
      </div>

      {/* Tabela de membros do grupo */}
      {showGroupSection && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-4">{t('clientData.groupMembers')}</h4>
          <DynamicTable
            columns={groupMemberColumns}
            data={data.groupMembers || []}
            onChange={onGroupMembersChange}
            createNewItem={createNewGroupMember}
            addButtonText={t('clientData.addMember')}
            disabled={disabled}
          />
        </div>
      )}

      {/* Preferência de contato */}
      <div className="mt-6 border-t pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-4">{t('clientData.contactPreference')}</h4>
        <RadioGroup
          name="canContact"
          label={t('clientData.canContact')}
          options={[
            { value: 'true', label: t('common.yes') },
            { value: 'false', label: t('common.no') },
          ]}
          value={data.contactPreference?.canContact?.toString() || 'false'}
          onChange={handleCanContactChange}
          orientation="horizontal"
          disabled={disabled}
        />

        {/* Campos de contato expandidos */}
        {showContactSection && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={t('clientData.contactName')}
              value={data.contactPreference?.contactName || ''}
              onChange={onContactPreferenceChange('contactName')}
              placeholder={t('clientData.contactName')}
              error={errors['clientData.contactPreference.contactName']}
              required
              disabled={disabled}
            />
            <Input
              label={t('clientData.contactPosition')}
              value={data.contactPreference?.contactPosition || ''}
              onChange={onContactPreferenceChange('contactPosition')}
              placeholder={t('clientData.contactPosition')}
              disabled={disabled}
            />
            <Input
              label={t('clientData.contactEmail')}
              value={data.contactPreference?.contactEmail || ''}
              onChange={onContactPreferenceChange('contactEmail')}
              placeholder="email@exemplo.com"
              type="email"
              error={errors['clientData.contactPreference.contactEmail']}
              required
              disabled={disabled}
            />
            <Input
              label={t('clientData.contactPhone')}
              value={data.contactPreference?.contactPhone || ''}
              onChange={onContactPreferenceChange('contactPhone')}
              mask={maskPhone}
              placeholder="(00) 00000-0000"
              error={errors['clientData.contactPreference.contactPhone']}
              required
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDataSection;
